"use client";

import * as React from "react";
import { i18n } from "@/i18n";
import { ICON_LIBRARY } from "../../constants/widget-config";
import { useWidgetStore } from "../../stores/widget-store";
import DesktopPreview from "@/assets/desktop-preview";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MobilePreview from "@/assets/mobile-preview";
import type { WidgetConfig } from "@/types/widget";
import { Monitor, Smartphone } from "lucide-react";


export function LivePreview() {
  const iconId = useWidgetStore((state) => state.config.iconId);
  const iconSize = useWidgetStore((state) => state.config.iconSize);
  const primaryColor = useWidgetStore((state) => state.config.primaryColor);
  const position = useWidgetStore((state) => state.config.position);
  const margins = useWidgetStore((state) => state.config.margins);
  const activeDevice = useWidgetStore((state) => state.activeDevice);
  const setActiveDevice = useWidgetStore((state) => state.setActiveDevice);
  const selectedIconComponent = ICON_LIBRARY.find(
    (icon) => icon.id === iconId
  )?.icon;

  return (
    <div className="cy:sticky cy:top-14">
      <div className="cy:space-y-4">
        <Tabs defaultValue="desktop" value={activeDevice} onValueChange={(value) => setActiveDevice(value as keyof WidgetConfig["position"])}>
          <TabsList className="cy:w-full cy:flex cy:justify-start cy:bg-white cy:border cy:rounded-md cy:p-0">
            <TabsTrigger
              className="cy:shadow-none cy:data-[state=active]:shadow-none cy:data-[state=active]:bg-primary cy:data-[state=active]:text-white cy:rounded-md cy:px-4 cy:py-2 cy:text-sm!"
              value="desktop"
            >
              <Monitor className="cy:h-4 cy:w-4" />
              {i18n.__( 'Desktop', 'accessibility-widget' )}
            </TabsTrigger>
            <TabsTrigger
              className="cy:shadow-none cy:data-[state=active]:shadow-none cy:data-[state=active]:bg-primary cy:data-[state=active]:text-white cy:rounded-md cy:px-4 cy:py-2 cy:text-sm!"
              value="mobile"
            >   
              <Smartphone className="cy:h-4 cy:w-4" />
              {i18n.__( 'Mobile', 'accessibility-widget' )}
            </TabsTrigger>
          </TabsList>
          <TabsContent 
            value="desktop" 
            className="cy:pt-8 cy:transition-all cy:duration-1000 cy:ease-in-out cy:transform cy:data-[state=inactive]:opacity-0 cy:data-[state=inactive]:scale-95 cy:data-[state=active]:opacity-100 cy:data-[state=active]:scale-100"
          >
            {/* Widget Preview */}
            <div className="cy:relative cy:bg-gray-100 cy:rounded-lg cy:w-[336px] cy:h-[250px] cy:border-gray-300">
              <div className="cy:absolute cy:inset-0 cy:flex cy:items-center cy:justify-center cy:text-gray-400">
                {/* SVG background */}
                <div className="cy:absolute cy:inset-0 cy:z-0 ">
                  <DesktopPreview />
                </div>
              </div>

              {/* Widget Button Preview */}
              <div
                className="cy:absolute"
                style={{
                  [position[activeDevice].includes("top")
                    ? "top"
                    : "bottom"]: `${
                    margins[activeDevice][
                      position[activeDevice].includes("top") ? "top" : "bottom"
                    ] + 5
                  }px`,
                  [position[activeDevice].includes("left")
                    ? "left"
                    : "right"]: `${
                    margins[activeDevice][
                      position[activeDevice].includes("left") ? "left" : "right"
                    ] + 5
                  }px`,
                }}
              >
                <button
                  className="cy:rounded-full cy:shadow-lg cy:transition-all cy:hover:scale-105 cy:flex cy:items-center cy:justify-center cy:border-1 cy:border-white"
                  style={{
                    backgroundColor: primaryColor,
                    width: `${iconSize}px`,
                    height: `${iconSize}px`,
                    outline: `5px solid ${primaryColor}`,
                  }}
                >
                  {selectedIconComponent &&
                    React.createElement(selectedIconComponent, {
                      className: "cy:text-white",
                      style: {
                        width: "60%",
                      },
                    })}
                </button>
              </div>
            </div>
          </TabsContent>
          <TabsContent 
            value="mobile" 
            className="cy:pt-8 cy:transition-all cy:duration-1000 cy:ease-in-out cy:transform cy:data-[state=inactive]:opacity-0 cy:data-[state=inactive]:scale-95 cy:data-[state=active]:opacity-100 cy:data-[state=active]:scale-100"
          >
            <div className="cy:relative cy:bg-gray-100 cy:rounded-lg  cy:w-[186px] cy:h-[332px]  cy:border-gray-300 cy:justify-self-center">
              <div className="cy:absolute cy:inset-0 cy:flex cy:items-center cy:justify-center cy:text-gray-400">
                {/* SVG background */}
                <div className="cy:absolute cy:inset-0 cy:z-0 ">
                  <MobilePreview />
                </div>
              </div>

              {/* Widget Button Preview */}
              <div
                className="cy:absolute"
                style={{
                  [position[activeDevice].includes("top")
                    ? "top"
                    : "bottom"]: `${
                    margins[activeDevice][
                      position[activeDevice].includes("top") ? "top" : "bottom"
                    ] + 5
                  }px`,
                  [position[activeDevice].includes("left")
                    ? "left"
                    : "right"]: `${
                    margins[activeDevice][
                      position[activeDevice].includes("left") ? "left" : "right"
                    ] + 5
                  }px`,
                }}
              >
                <button
                  className="cy:rounded-full cy:shadow-lg cy:transition-all cy:hover:scale-105 cy:flex cy:items-center cy:justify-center cy:border-1 cy:border-white"
                  style={{
                    backgroundColor: primaryColor,
                    width: `${iconSize}px`,
                    height: `${iconSize}px`,
                    outline: `5px solid ${primaryColor}`,
                  }}
                >
                  {selectedIconComponent &&
                    React.createElement(selectedIconComponent, {
                      className: "cy:text-white",
                      style: {
                        width: `${iconSize * 0.5}px`,
                        height: `${iconSize * 0.5}px`,
                      },
                    })}
                </button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
